import { z } from 'zod';

/**
 * Avisos de la administración a los residentes.
 *
 * El administrador escribe, elige a quién va —una vivienda, varias, o todas— y
 * decide si es solo para los titulares o para todos los que viven ahí.
 */

/**
 * Lo más que puede pesar un adjunto.
 *
 * **Dos megas dan para muy poco video**: unos segundos en calidad baja. Es el
 * límite que pidió el dueño y se respeta, pero conviene saberlo antes de
 * prometerle a nadie que puede mandar el video de la asamblea.
 *
 * El tope existe por el enlace del residente, no por el disco: un aviso a
 * doscientas viviendas con un adjunto de veinte megas son cuatro gigas
 * bajándose por datos móviles que paga cada quien.
 */
export const ADJUNTO_MAX_BYTES = 2 * 1024 * 1024;

/** Lo que se acepta como adjunto, y nada más. */
export const ADJUNTO_MIMES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'video/mp4',
  'video/webm',
] as const;

export const alcanceMensajeSchema = z.enum(['TITULARES', 'TODOS']);
export type AlcanceMensaje = z.infer<typeof alcanceMensajeSchema>;

export const crearMensajeSchema = z
  .object({
    titulo: z.string().trim().min(1, 'Ponle un título').max(120),
    cuerpo: z.string().trim().min(1, 'El aviso no puede ir vacío').max(4000),
    alcance: alcanceMensajeSchema.default('TODOS'),
    /**
     * A todas las viviendas. Con esto en falso mandan `unidades`.
     *
     * Es una bandera y no «la lista de todas» porque un aviso para todos tiene
     * que alcanzar también a las viviendas que se den de alta **después** de
     * escribirlo. Guardando la lista del momento se congelaría el condominio
     * tal como estaba ese martes.
     */
    paraTodas: z.boolean().default(false),
    unidades: z.array(z.string().min(1).max(64)).max(5000).default([]),
  })
  .refine((m) => m.paraTodas || m.unidades.length > 0, {
    message: 'Elige a qué viviendas va, o márcalo para todas',
    path: ['unidades'],
  });

export type CrearMensaje = z.infer<typeof crearMensajeSchema>;

/** Editar un borrador: lo mismo, y todo opcional. */
export const editarMensajeSchema = z.object({
  titulo: z.string().trim().min(1).max(120).optional(),
  cuerpo: z.string().trim().min(1).max(4000).optional(),
  alcance: alcanceMensajeSchema.optional(),
  paraTodas: z.boolean().optional(),
  unidades: z.array(z.string().min(1).max(64)).max(5000).optional(),
});

export type EditarMensaje = z.infer<typeof editarMensajeSchema>;

export interface AdjuntoMensaje {
  id: string;
  tipo: 'IMAGEN' | 'VIDEO';
  mime: string;
  bytes: number;
}

/** Lo que ve el administrador en su lista. */
export interface MensajeResumen {
  id: string;
  titulo: string;
  cuerpo: string;
  alcance: AlcanceMensaje;
  paraTodas: boolean;
  unidades: number;
  adjuntos: AdjuntoMensaje[];
  publicadoEn: string | null;
  creadoEn: string;
  autor: string | null;
  /** Cuántos lo recibieron y cuántos lo abrieron. Cero mientras es borrador. */
  entregados: number;
  leidos: number;
}

/**
 * Un aviso con todo lo que hace falta para volver a abrirlo y corregirlo.
 *
 * Lleva **cuáles** son las viviendas elegidas, no cuántas: la lista solo enseña
 * el número, y para corregir hacen falta las de verdad.
 */
export interface MensajeGuardado {
  id: string;
  titulo: string;
  cuerpo: string;
  alcance: AlcanceMensaje;
  paraTodas: boolean;
  publicadoEn: string | null;
  adjuntos: AdjuntoMensaje[];
  unidades: Array<{ id: string; clave: string }>;
}

/** Lo que ve el residente. */
export interface MensajeParaResidente {
  id: string;
  titulo: string;
  cuerpo: string;
  adjuntos: AdjuntoMensaje[];
  publicadoEn: string;
  leidoEn: string | null;
}
